package com.arena_web.ui.sections.dashboard

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

// Figma muted text color: text_on-surface / muted = #99B1BF
private val MutedTextColor = Color.hex("99B1BF")

// Figma: rounded-22, border, gradient, pt-12 pb-22 px-12, justify-between, overflow-clip
@Composable
private fun StatCardContainer(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .sBorderRadius(22)
            .styleModifier {
                property("padding", "${s(12)} ${s(12)} ${s(22)} ${s(12)}")
                property("box-sizing", "border-box")
                property("overflow", "clip")
                property("justify-content", "space-between")
                property("background",
                    "linear-gradient(184deg, rgba(60, 121, 241, 0.135) 20.1%, rgba(33, 65, 130, 0.077) 82.9%, rgba(5, 4, 12, 0.015) 109%)")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
            }
    ) {
        content()
    }
}

// Header row: title (22px Medium, #bed6f5) + icon (24px)
@Composable
private fun StatCardHeader(title: String, icon: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            title,
            modifier = Modifier
                .flexGrow(1)
                .sFontSize(22)
                .fontWeight(AppTypography.Medium)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
        Icon(src = icon, modifier = Modifier.sSize(24))
    }
}

// =============================================================================
// Rating card — unique structure: "4.7★" + "128 reviews"
// =============================================================================

@Composable
private fun RatingStatCard(
    rating: String = "—",
    reviews: String = "—"
) {
    val t = LocalAppStrings.current
    StatCardContainer {
        // Header
        StatCardHeader(title = t.dashboardRatingTitle, icon = AppIcons.Star)

        // Content: gap-18 between value row and reviews row
        Column(modifier = Modifier.sGap(18)) {
            // Rating value: "4.7" + small star
            Row(
                modifier = Modifier.sGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    rating,
                    modifier = Modifier
                        .sFontSize(26)
                        .fontWeight(AppTypography.SemiBold)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                Icon(src = AppIcons.StarRating16, modifier = Modifier.sSize(16))
            }

            // Reviews: "128 reviews"
            Row(
                modifier = Modifier.sGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    reviews,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Regular)
                        .color(MutedTextColor)
                )
                SpanText(
                    t.dashboardReviews,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Regular)
                        .color(MutedTextColor)
                )
            }
        }
    }
}

// =============================================================================
// Stat card with change percentage — Avg Duration, Total Revenue, Total Bookings
// =============================================================================

@Composable
private fun ChangeStatCard(
    title: String,
    value: String,
    changeText: String,
    icon: String
) {
    StatCardContainer {
        // Header
        StatCardHeader(title = title, icon = icon)

        // Content: gap-18 between change row and value
        Column(modifier = Modifier.sGap(18)) {
            // Change row: blue dot (4px) + change text (18px) + trending-up icon (24px)
            Row(
                modifier = Modifier.fillMaxWidth().sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Blue dot indicator
                Box(
                    modifier = Modifier
                        .sSize(4)
                        .sBorderRadius(100)
                        .backgroundColor(AppColors.Primary)
                )
                // Change percentage text
                SpanText(
                    changeText,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Regular)
                        .color(MutedTextColor)
                )
                // Trending-up icon
                Icon(src = AppIcons.TrendingUp, modifier = Modifier.sSize(24))
            }

            // Large value
            SpanText(
                value,
                modifier = Modifier
                    .sFontSize(26)
                    .fontWeight(AppTypography.SemiBold)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
    }
}

// =============================================================================
// 2x2 grid — Figma: gap-8, each card 300x205
// =============================================================================

@Composable
fun DashboardStatCardsGrid(
    revenue: String? = null,
    bookingCount: Long? = null,
    averageRating: Double? = null,
    totalRatingsCount: Int? = null
) {
    val t = LocalAppStrings.current
    val formattedRevenue = revenue?.let {
        val num = it.toDoubleOrNull()
        if (num != null) "$ ${formatNumber(num)}" else "$ $it"
    } ?: "—"

    val formattedRating = averageRating?.let {
        val whole = it.toLong()
        val frac = ((it - whole) * 10).toLong()
        "$whole.$frac"
    } ?: "—"

    val formattedBookings = bookingCount?.toString() ?: "—"
    val formattedReviews = totalRatingsCount?.toString() ?: "—"

    Column(
        modifier = Modifier.fillMaxSize().sGap(8)
    ) {
        // Top row: Rating + Avg Duration
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .flexBasis(0.px)
                .sGap(8)
        ) {
            Box(modifier = Modifier.flexGrow(1).flexBasis(0.px).fillMaxHeight()) {
                RatingStatCard(
                    rating = formattedRating,
                    reviews = formattedReviews
                )
            }
            Box(modifier = Modifier.flexGrow(1).flexBasis(0.px).fillMaxHeight()) {
                ChangeStatCard(
                    title = t.dashboardAvgDuration,
                    value = "—",
                    changeText = "—",
                    icon = AppIcons.Time
                )
            }
        }
        // Bottom row: Total Revenue + Total Bookings
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .flexBasis(0.px)
                .sGap(8)
        ) {
            Box(modifier = Modifier.flexGrow(1).flexBasis(0.px).fillMaxHeight()) {
                ChangeStatCard(
                    title = t.dashboardTotalRevenue,
                    value = formattedRevenue,
                    changeText = "—",
                    icon = AppIcons.Dollar
                )
            }
            Box(modifier = Modifier.flexGrow(1).flexBasis(0.px).fillMaxHeight()) {
                ChangeStatCard(
                    title = t.dashboardTotalBookings,
                    value = formattedBookings,
                    changeText = "—",
                    icon = AppIcons.Calendar
                )
            }
        }
    }
}

private fun formatNumber(num: Double): String {
    val intPart = num.toLong()
    return intPart.toString().reversed().chunked(3).joinToString(" ").reversed()
}
